"use client";
import {
  __async,
  __spreadProps,
  __spreadValues
} from "../../../chunk-MMUBH76A.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import {
  entityToWallet,
  OnRampProvider,
  OnRampAsset,
  Network,
  EnabledFlow
} from "@getpara/web-sdk";
import { useModalStore, useUserInfoStore } from "../../stores/index.js";
import { ModalStep } from "../../utils/steps.js";
import { Body } from "../Body/Body.js";
import { Footer } from "../Footer/Footer.js";
import { DEFAULTS } from "../../constants/defaults.js";
import { useGoBack } from "../../hooks/useGoBack.js";
import { useInternalClient } from "../../../provider/hooks/utils/useInternalClient.js";
import { useEmbeddedExternalConnection } from "../../hooks/useEmbeddedExternalConnection.js";
import { useCreateAccount } from "../../hooks/useCreateAccount.js";
import { formatBiometricHints } from "@getpara/react-common";
import { useExternalWallets } from "../../providers/ExternalWalletContext.js";
function isRampConfig(config) {
  return "hostApiKey" in config;
}
const AssetNetworks = {
  [OnRampAsset.SOLANA]: Network.SOLANA,
  [OnRampAsset.ATOM]: Network.COSMOS,
  [OnRampAsset.CELO]: Network.CELO,
  [OnRampAsset.POLYGON]: Network.POLYGON
};
const AssetMap = {
  SOLANA: OnRampAsset.SOLANA,
  SOL: OnRampAsset.SOLANA,
  ATOM: OnRampAsset.ATOM,
  CELO: OnRampAsset.CELO,
  POLYGON: OnRampAsset.POLYGON,
  MATIC: OnRampAsset.POLYGON,
  USDC: OnRampAsset.USDC,
  ETH: OnRampAsset.ETHEREUM,
  ETHEREUM: OnRampAsset.ETHEREUM
};
const ModalContent = forwardRef(
  ({
    onRampConfig: propsOnRampConfig,
    twoFactorAuthEnabled = false,
    recoverySecretStepEnabled = false,
    oAuthMethods,
    disableEmailLogin,
    disablePhoneLogin,
    onClose,
    onRampTestMode,
    loginTransitionOverride,
    createWalletOverride
  }, ref) => {
    var _a;
    const para = useInternalClient();
    const refs = useModalStore((state) => state.refs);
    const currentStep = useModalStore((state) => state.step);
    const webAuthURLForLogin = useModalStore((state) => state.webAuthURLForLogin);
    const passwordUrlForLogin = useModalStore((state) => state.passwordUrlForLogin);
    const isLogin = useModalStore((state) => state.isLogin());
    const onRampConfig = useModalStore((state) => state.onRampConfig);
    const setStep = useModalStore((state) => state.setStep);
    const setBiometricLocationHints = useModalStore((state) => state.setBiometricLocationHints);
    const setWebAuthURLForLogin = useModalStore((state) => state.setWebAuthURLForLogin);
    const setWebAuthURLForCreate = useModalStore((state) => state.setWebAuthURLForCreate);
    const setPasswordUrlForLogin = useModalStore((state) => state.setPasswordUrlForLogin);
    const setIFrameUrl = useModalStore((state) => state.setIFrameUrl);
    const setSupportedAuthMethods = useModalStore((state) => state.setSupportedAuthMethods);
    const setOnRampConfig = useModalStore((state) => state.setOnRampConfig);
    const accountAddFundTab = useModalStore((state) => state.accountAddFundTab);
    const setAccountAddFundTab = useModalStore((state) => state.setAccountAddFundTab);
    const setRecoveryShare = useUserInfoStore((state) => state.setRecoveryShare);
    const authStepRoute = useModalStore((state) => state.authStepRoute);
    const isIFrameReady = useModalStore((state) => state.isIFrameReady);
    const goBack = useGoBack();
    const createAccount = useCreateAccount();
    const biometricLocationHints = useModalStore((state) => {
      var _a2;
      return (_a2 = state.biometricLocationHints) != null ? _a2 : [];
    });
    const formattedHints = useMemo(() => formatBiometricHints(biometricLocationHints), [biometricLocationHints]);
    const passkeysSupported = useModalStore((state) => state.isPasskeySupported);
    const [hasHints, isOnKnownDevice] = [(biometricLocationHints == null ? void 0 : biometricLocationHints.length) > 0, (_a = formattedHints == null ? void 0 : formattedHints.isOnKnownDevice) != null ? _a : false];
    const { disconnectExternalWallet } = useExternalWallets();
    const [walletCreationInProgress, setWalletCreationInProgress] = useState(false);
    const connectEmbeddedToExternalConnectors = useEmbeddedExternalConnection();
    useEffect(() => {
      if (!!authStepRoute && isIFrameReady) {
        setTimeout(() => {
          setStep(authStepRoute);
        }, 200);
      }
    }, [authStepRoute, isIFrameReady]);
    useImperativeHandle(ref, () => {
      return {
        handleModalClose() {
          handleClose();
        }
      };
    }, []);
    const is2FASetup = () => __async(void 0, null, function* () {
      if (!twoFactorAuthEnabled) {
        return true;
      }
      try {
        const { isSetup } = yield para.check2FAStatus();
        return isSetup;
      } catch (error) {
        console.error("An error occurred while checking 2FA:", error);
        return false;
      }
    });
    function awaitLoginTransition() {
      return __async(this, null, function* () {
        const { isComplete, isError, needsWallet } = yield para.waitForLoginAndSetup();
        if (isError) {
          [ModalStep.AWAITING_BIOMETRIC_LOGIN, ModalStep.AWAITING_PASSWORD_LOGIN].includes(refs.currentStep.current) && goBack();
          return;
        }
        if (isComplete) {
          setWebAuthURLForLogin("");
          setPasswordUrlForLogin("");
          setSupportedAuthMethods(/* @__PURE__ */ new Set());
          setBiometricLocationHints();
          if (needsWallet && !para.isNoWalletConfig) {
            setStep(ModalStep.AWAITING_WALLET_CREATION);
          } else {
            yield connectEmbeddedToExternalConnectors();
            if (yield is2FASetup()) {
              setStep(ModalStep.LOGIN_DONE);
            } else {
              setStep(ModalStep.SETUP_2FA);
            }
          }
        }
      });
    }
    const awaitWalletCreationTransition = () => __async(void 0, null, function* () {
      var _a2;
      const isComplete = yield para.waitForAccountCreation();
      window.clearTimeout((_a2 = refs.poll.current) == null ? void 0 : _a2.timeout);
      refs.poll.current = void 0;
      refs.popupWindow.current = void 0;
      if (isComplete) {
        setWebAuthURLForCreate("");
        setIFrameUrl("");
        if (para.isNoWalletConfig) {
          const is2faComplete = twoFactorAuthEnabled ? (yield para.check2FAStatus()).isSetup : true;
          setStep(is2faComplete ? ModalStep.LOGIN_DONE : ModalStep.SETUP_2FA);
        } else {
          setStep(ModalStep.AWAITING_WALLET_CREATION);
        }
      } else if (refs.currentStep.current === ModalStep.AWAITING_BIOMETRIC_CREATION) {
        goBack();
      }
    });
    useEffect(() => {
      if (currentStep !== ModalStep.AWAITING_WALLET_CREATION || walletCreationInProgress) {
        return;
      }
      function genWallet() {
        return __async(this, null, function* () {
          setWalletCreationInProgress(true);
          let recoverySecret, walletIds;
          if (!createWalletOverride) {
            const created = yield para.waitForPasskeyAndCreateWallet();
            recoverySecret = created.recoverySecret;
            walletIds = created.walletIds;
          } else {
            const created = yield createWalletOverride(para);
            const fetchedWallets = (yield para.fetchWallets()).filter((wallet) => !!wallet.address);
            const newWallets = {};
            for (const wallet of fetchedWallets) {
              newWallets[wallet.id] = __spreadProps(__spreadValues({}, entityToWallet(wallet)), {
                signer: ""
              });
            }
            para.setWallets(newWallets);
            recoverySecret = created.recoverySecret;
            walletIds = created.walletIds;
          }
          yield para.setCurrentWalletIds(walletIds);
          yield connectEmbeddedToExternalConnectors();
          if (recoverySecretStepEnabled) {
            setRecoveryShare(recoverySecret);
          }
          setWalletCreationInProgress(false);
          if (!recoverySecret || !recoverySecretStepEnabled) {
            setStep(ModalStep.WALLET_CREATION_DONE);
          } else {
            setStep(ModalStep.SECRET);
          }
        });
      }
      genWallet();
    }, [isLogin, currentStep]);
    useEffect(() => {
      const isAwaitingLogin = [
        ModalStep.AWAITING_PASSWORD_LOGIN,
        ModalStep.AWAITING_BIOMETRIC_LOGIN,
        ModalStep.BIOMETRIC_LOGIN
      ].includes(currentStep);
      const isUnknownDeviceWithHints = hasHints && !isOnKnownDevice;
      const isPasskeyUnsupported = !passkeysSupported;
      const hasLoginURLs = !!webAuthURLForLogin || !!passwordUrlForLogin;
      const userNeedsToLogin = isAwaitingLogin || isUnknownDeviceWithHints || isPasskeyUnsupported;
      if (userNeedsToLogin && hasLoginURLs) {
        if (loginTransitionOverride) {
          function loginOverride() {
            return __async(this, null, function* () {
              yield loginTransitionOverride(para);
              setWebAuthURLForLogin("");
              setPasswordUrlForLogin("");
              setBiometricLocationHints();
              yield connectEmbeddedToExternalConnectors();
              if (yield is2FASetup()) {
                setStep(ModalStep.LOGIN_DONE);
              } else {
                setStep(ModalStep.SETUP_2FA);
              }
            });
          }
          loginOverride();
          return;
        }
        if (typeof window !== "undefined") {
          refs.poll.current = {
            action: "login",
            timeout: window.setTimeout(awaitLoginTransition, DEFAULTS.LOGGIN_POLLING_DELAY_MS)
          };
        }
        return () => {
          var _a2;
          if (typeof window !== "undefined" && !!refs.poll.current) {
            window.clearTimeout((_a2 = refs.poll.current) == null ? void 0 : _a2.timeout);
          }
          para.exitLogin();
        };
      }
    }, [currentStep, webAuthURLForLogin, passwordUrlForLogin]);
    useEffect(() => {
      var _a2;
      if (![ModalStep.BIOMETRIC_CREATION, ModalStep.AWAITING_BIOMETRIC_CREATION, ModalStep.PASSWORD_CREATION].includes(
        currentStep
      )) {
        return;
      }
      if (((_a2 = refs.poll.current) == null ? void 0 : _a2.action) === "signup") {
        return;
      }
      refs.poll.current = {
        action: "signup",
        timeout: window.setTimeout(awaitWalletCreationTransition, DEFAULTS.POLLING_INTERVAL_MS)
      };
    }, [currentStep]);
    const handleClose = () => {
      onClose();
      if (currentStep === ModalStep.EXTERNAL_WALLET_VERIFICATION && para.isExternalWalletAuth) {
        disconnectExternalWallet();
      }
    };
    useEffect(() => {
      var _a2, _b, _c;
      refs.currentStep.current = currentStep;
      let resetPoll = false;
      if (![ModalStep.BIOMETRIC_CREATION, ModalStep.AWAITING_BIOMETRIC_CREATION, ModalStep.PASSWORD_CREATION].includes(
        currentStep
      )) {
        para.exitAccountCreation();
        resetPoll = ["signup"].includes((_a2 = refs.poll.current) == null ? void 0 : _a2.action);
      }
      if (![ModalStep.AWAITING_PASSWORD_LOGIN, ModalStep.AWAITING_BIOMETRIC_LOGIN, ModalStep.BIOMETRIC_LOGIN].includes(
        currentStep
      )) {
        para.exitLogin();
        resetPoll = ((_b = refs.poll.current) == null ? void 0 : _b.action) === "login";
      }
      if (![ModalStep.AWAITING_OAUTH, ModalStep.FARCASTER_OAUTH].includes(currentStep)) {
        para.exitOAuth();
      }
      if (currentStep === ModalStep.PASSWORD_CREATION) {
        createAccount.withPassword();
      }
      if (resetPoll && typeof window !== "undefined") {
        window.clearTimeout((_c = refs.poll.current) == null ? void 0 : _c.timeout);
        refs.poll.current = void 0;
      }
    }, [currentStep]);
    useEffect(() => {
      if (!onRampConfig) {
        para.ctx.client.getOnRampConfig().then((res) => {
          var _a2, _b;
          let newOnRampConfig;
          if (!!propsOnRampConfig) {
            const { enabledFlows, network, asset, providers, testMode } = propsOnRampConfig;
            const rampConfig = providers.find((config) => isRampConfig(config));
            newOnRampConfig = {
              isBuyEnabled: !enabledFlows || enabledFlows.some((str) => EnabledFlow[str] === EnabledFlow.BUY),
              isReceiveEnabled: !enabledFlows || enabledFlows.some((str) => EnabledFlow[str] === EnabledFlow.RECEIVE),
              isWithdrawEnabled: !enabledFlows || enabledFlows.some((str) => EnabledFlow[str] === EnabledFlow.WITHDRAW),
              allowedAssets: network ? { [Network[network]]: asset ? [AssetMap[asset]] : true } : asset ? { [(_a2 = AssetNetworks[AssetMap[asset]]) != null ? _a2 : Network.ETHEREUM]: [AssetMap[asset]] } : res.allowedAssets,
              assetInfo: res.assetInfo,
              providers: providers.map(({ id }) => OnRampProvider[id]),
              rampApiKey: (_b = rampConfig == null ? void 0 : rampConfig.hostApiKey) != null ? _b : res.rampApiKey,
              testMode: testMode != null ? testMode : onRampTestMode
            };
          } else {
            newOnRampConfig = __spreadProps(__spreadValues({}, res), { testMode: onRampTestMode });
          }
          setOnRampConfig(newOnRampConfig);
          if (!accountAddFundTab) {
            setAccountAddFundTab(
              newOnRampConfig.isBuyEnabled ? EnabledFlow.BUY : newOnRampConfig.isReceiveEnabled ? EnabledFlow.RECEIVE : newOnRampConfig.isWithdrawEnabled ? EnabledFlow.WITHDRAW : void 0
            );
          }
        }).catch();
      }
    }, []);
    useEffect(() => {
      if (!!onRampConfig) {
        setOnRampConfig(__spreadProps(__spreadValues({}, onRampConfig), { testMode: onRampTestMode }));
      }
    }, [onRampTestMode]);
    useEffect(() => {
      const init = () => __async(void 0, null, function* () {
        if (!(yield para.isFullyLoggedIn())) {
          yield disconnectExternalWallet();
        }
      });
      init();
      return () => {
        var _a2;
        window.clearTimeout((_a2 = refs.poll.current) == null ? void 0 : _a2.timeout);
        para.exitLoops();
      };
    }, []);
    useEffect(() => {
      const checkPopup = window.setInterval(() => {
        if (refs.popupWindow.current && refs.popupWindow.current.window.closed) {
          const { type } = refs.popupWindow.current;
          const stepRef = refs.currentStep.current;
          switch (true) {
            case (type === "CREATE_PASSKEY" && stepRef === ModalStep.AWAITING_BIOMETRIC_CREATION):
            case (type === "LOGIN_PASSKEY" && stepRef === ModalStep.AWAITING_BIOMETRIC_LOGIN):
            case (type === "LOGIN_PASSWORD" && stepRef === ModalStep.AWAITING_PASSWORD_LOGIN):
              goBack();
              break;
          }
          refs.popupWindow.current = void 0;
        }
      }, 3200);
      return () => {
        window.clearInterval(checkPopup);
      };
    }, []);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Body,
        {
          oAuthMethods,
          twoFactorAuthEnabled,
          disableEmailLogin,
          disablePhoneLogin,
          onClose: handleClose
        }
      ),
      /* @__PURE__ */ jsx(Footer, {})
    ] });
  }
);
export {
  ModalContent
};
